import { useState, useMemo, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api, fmt, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import { inputCls } from "@/components/Modal";
import { TableCard, StatusBadge, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { History, Search, Package, Boxes, TrendingUp, AlertTriangle, ArrowUpFromLine, ChevronRight } from "lucide-react";

const PAGE = 20;

function Stat({ label, value, icon: Icon, tone = "forest", hint }) {
  const toneCls = tone === "danger" ? "text-agri-danger bg-red-50" : tone === "amber" ? "text-agri-amber-dark bg-agri-amber/10" : "text-agri-forest bg-agri-forest/10";
  return (
    <div className="card-soft p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-cap">{label}</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-agri-ink truncate">{value}</div>
          {hint && <div className="text-[11px] text-agri-ink-2 mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${toneCls}`}><Icon size={18} /></div>
      </div>
    </div>
  );
}

const STOCK_PILL = {
  ok: "bg-green-50 text-agri-forest",
  low: "bg-amber-50 text-agri-amber-dark",
  out: "bg-red-50 text-agri-danger",
};
const STOCK_LABEL = { ok: "In stock", low: "Low", out: "Out" };
function StockPill({ status }) {
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STOCK_PILL[status] || STOCK_PILL.ok}`}>{STOCK_LABEL[status] || status}</span>;
}

const SORTS = {
  revenue: { label: "Top revenue", fn: (a, b) => b.revenue - a.revenue },
  profit: { label: "Top profit", fn: (a, b) => b.profit - a.profit },
  sold: { label: "Most sold", fn: (a, b) => b.net_sold - a.net_sold },
  low: { label: "Lowest stock", fn: (a, b) => a.on_hand - b.on_hand },
  value: { label: "Stock value", fn: (a, b) => b.stock_value_cost - a.stock_value_cost },
  name: { label: "Name (A–Z)", fn: (a, b) => a.name.localeCompare(b.name) },
};

/* ---------- per-product detail (KPIs + movement history) ---------- */
function ProductDetail({ product }) {
  const { data: movements = [] } = useQuery({
    queryKey: ["movements", "product", product.id],
    queryFn: () => api.get("/stock/movements", { params: { product_id: product.id, limit: 200 } }).then((r) => r.data),
  });
  const metric = [
    { label: "On hand", value: `${fmt(product.on_hand)} ${product.unit}`, hint: `reorder at ${fmt(product.reorder_level)}` },
    { label: "Purchased", value: `${fmt(product.purchased)} ${product.unit}`, hint: product.purchase_returned ? `${fmt(product.purchase_returned)} returned to supplier` : "lifetime in" },
    { label: "Sold", value: `${fmt(product.net_sold)} ${product.unit}`, hint: product.sale_returned ? `${fmt(product.sale_returned)} customer returns` : "lifetime out" },
    { label: "Revenue", value: fmtMoney(product.revenue), hint: "from this product" },
    { label: "COGS", value: fmtMoney(product.cogs), hint: "cost of units sold" },
    { label: "Profit", value: fmtMoney(product.profit), hint: `${product.margin}% margin`, tone: product.profit < 0 ? "danger" : "forest" },
    { label: "Stock value (cost)", value: fmtMoney(product.stock_value_cost), hint: `${fmtMoney(product.purchase_price)}/${product.unit} cost` },
    { label: "Stock value (retail)", value: fmtMoney(product.stock_value_retail), hint: `${fmtMoney(product.selling_price)}/${product.unit} sell` },
    { label: "Unrealized profit", value: fmtMoney(product.potential_profit), hint: "if current stock sells" },
  ];
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-agri-ink-2">
        <span className="px-2 py-0.5 rounded bg-black/5">{product.category}</span>
        {product.brand && <span className="px-2 py-0.5 rounded bg-black/5">{product.brand}</span>}
        <span>SKU {product.sku || "—"}</span>
        <StockPill status={product.status} />
        {product.expiry_date && <span className={product.expired ? "text-agri-danger font-medium" : ""}>Expiry {product.expiry_date}{product.expired ? " (expired)" : ""}</span>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {metric.map((m) => (
          <div key={m.label} className="rounded-lg border border-[rgba(28,28,30,0.08)] p-3">
            <div className="label-cap">{m.label}</div>
            <div className={`mt-1 text-lg font-bold tabular-nums ${m.tone === "danger" ? "text-agri-danger" : "text-agri-ink"}`}>{m.value}</div>
            {m.hint && <div className="text-[11px] text-agri-ink-2 mt-0.5">{m.hint}</div>}
          </div>
        ))}
      </div>

      <div>
        <div className="label-cap mb-2">Movement history</div>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-[rgba(28,28,30,0.08)]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-agri-cream/80 backdrop-blur">
              <tr>
                <th className="text-left px-3 py-2 label-cap">Date</th>
                <th className="text-left px-3 py-2 label-cap">Type</th>
                <th className="text-right px-3 py-2 label-cap">Qty</th>
                <th className="text-left px-3 py-2 label-cap">Reason</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-agri-ink-2">No movements recorded.</td></tr>}
              {movements.map((m) => (
                <tr key={m.id} className="border-t border-[rgba(28,28,30,0.05)]">
                  <td className="px-3 py-2 text-agri-ink-2 whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2"><StatusBadge status={m.type} /></td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmt(m.quantity)}</td>
                  <td className="px-3 py-2 text-agri-ink-2">{m.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------- raw movement log (all products) ---------- */
function MovementLog() {
  const [page, setPage] = useState(0);
  const { data } = useQuery({
    queryKey: ["movements", "log", page],
    queryFn: () => api.get("/stock/movements", { params: { skip: page * PAGE, limit: PAGE } })
      .then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] ?? r.data.length) })),
    placeholderData: keepPreviousData,
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <>
      <div className="text-xs text-agri-ink-2">{total} movement{total === 1 ? "" : "s"}</div>
      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Date</th>
            <th className={thCls}>Product</th>
            <th className={thCls}>Type</th>
            <th className={thNumCls}>Qty</th>
            <th className={thCls}>Reason</th>
            <th className={thCls}>By</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={6} icon={History}>No movements yet.</EmptyRow>}
          {rows.map((m) => (
            <tr key={m.id} className={rowCls}>
              <td data-label="Date" className={`${tdCls} text-agri-ink-2 whitespace-nowrap`}>{new Date(m.created_at).toLocaleString()}</td>
              <td data-label="Product" className={`${tdCls} font-medium`}>{m.product_name}</td>
              <td data-label="Type" className={tdCls}><StatusBadge status={m.type} /></td>
              <td data-label="Qty" className={`${tdNumCls} font-semibold`}>{fmt(m.quantity)}</td>
              <td data-label="Reason" className={`${tdCls} text-agri-ink-2`}>{m.reason}</td>
              <td data-label="By" className={`${tdCls} text-xs text-agri-ink-2`}>{m.user_email}</td>
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Pagination page={page} pageSize={PAGE} total={total} onPage={setPage} />
    </>
  );
}

/* ---------- product analytics dashboard ---------- */
function ProductAnalytics() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("revenue");
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["product-analytics"], queryFn: () => api.get("/reports/product-analytics").then((r) => r.data) });
  const totals = data?.totals;
  const products = useMemo(() => data?.products ?? [], [data]);
  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(), [products]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products
      .filter((p) => p.active)
      .filter((p) => cat === "all" || p.category === cat)
      .filter((p) => !needle || p.name.toLowerCase().includes(needle) || (p.sku || "").toLowerCase().includes(needle) || (p.brand || "").toLowerCase().includes(needle))
      .sort(SORTS[sort].fn);
  }, [products, q, cat, sort]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Products" value={totals?.products ?? "—"} icon={Package} hint="active in catalog" />
        <Stat label="Stock Value" value={fmtMoney(totals?.stock_value_cost || 0)} icon={Boxes} hint="on-hand at cost" />
        <Stat label="Units Sold" value={fmt(totals?.units_sold || 0)} icon={ArrowUpFromLine} tone="amber" hint="lifetime out" />
        <Stat label="Profit" value={fmtMoney(totals?.profit || 0)} icon={TrendingUp} tone={(totals?.profit || 0) < 0 ? "danger" : "forest"} hint="on goods sold" />
        <Stat label="Low / Out" value={totals?.low_stock ?? "—"} icon={AlertTriangle} tone={(totals?.low_stock || 0) > 0 ? "danger" : "forest"} hint="need restock" />
      </div>

      <div className="card-soft p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-agri-ink-2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, SKU, brand…" className={inputCls + " pl-9"} />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls + " max-w-[180px]"}>
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={inputCls + " max-w-[170px]"}>
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="text-xs text-agri-ink-2">{rows.length} product{rows.length === 1 ? "" : "s"}</div>
      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Product</th>
            <th className={thCls}>Stock</th>
            <th className={thNumCls}>On hand</th>
            <th className={thNumCls}>Purchased</th>
            <th className={thNumCls}>Sold</th>
            <th className={thNumCls}>Revenue</th>
            <th className={thNumCls}>Profit</th>
            <th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={8} icon={Package}>{isLoading ? "Loading…" : "No products found."}</EmptyRow>}
          {rows.map((p) => (
            <tr key={p.id} className={rowCls + " cursor-pointer"} onClick={() => setSelected(p)}>
              <td className={`${tdCls} rt-primary`}>
                <div className="font-medium text-agri-ink">{p.name}</div>
                <div className="text-xs text-agri-ink-2">{p.category}{p.brand ? ` · ${p.brand}` : ""}</div>
              </td>
              <td data-label="Stock" className={tdCls}><StockPill status={p.status} /></td>
              <td data-label="On hand" className={`${tdNumCls} font-semibold`}>{fmt(p.on_hand)}</td>
              <td data-label="Purchased" className={`${tdNumCls} text-agri-ink-2`}>{fmt(p.purchased)}</td>
              <td data-label="Sold" className={`${tdNumCls} text-agri-ink-2`}>{fmt(p.net_sold)}</td>
              <td data-label="Revenue" className={tdNumCls}>{fmtMoney(p.revenue)}</td>
              <td data-label="Profit" className={`${tdNumCls} font-semibold ${p.profit < 0 ? "text-agri-danger" : "text-agri-forest"}`}>{fmtMoney(p.profit)}</td>
              <td className={`${tdCls} text-right rt-actions`}><ChevronRight size={16} className="text-agri-ink-2 inline" /></td>
            </tr>
          ))}
        </tbody>
      </TableCard>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.name || ""} size="xl">
        {selected && <ProductDetail product={selected} />}
      </Modal>
    </div>
  );
}

export default function StockHistory() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [tab, setTab] = useState(canManage ? "products" : "log");
  useEffect(() => { if (!canManage) setTab("log"); }, [canManage]);

  return (
    <div className="space-y-6" data-testid="stock-history-page">
      <div>
        <div className="label-cap">Inventory Insights</div>
        <h1 className="font-heading text-3xl mt-1">Stock History</h1>
        <p className="text-sm text-agri-ink-2 mt-1">Per-product performance — purchases, sales, on-hand stock and profit.</p>
      </div>

      {canManage && (
        <div className="flex gap-1 border-b border-[rgba(28,28,30,0.08)]">
          {[["products", "Product Analytics", Package], ["log", "Movement Log", History]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === key ? "border-agri-forest text-agri-forest" : "border-transparent text-agri-ink-2 hover:text-agri-ink"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      )}

      {tab === "products" && canManage ? <ProductAnalytics /> : <MovementLog />}
    </div>
  );
}
